import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputAdornment,
  InputLabel,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BusinessIcon from '@mui/icons-material/Business';
import NotesIcon from '@mui/icons-material/Notes';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EventIcon from '@mui/icons-material/Event';
import TimelineIcon from '@mui/icons-material/Timeline';
import InsightsIcon from '@mui/icons-material/Insights';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import jsPDF from 'jspdf';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const pipelineStages = [
  'New Opportunity',
  'Discovery',
  'Qualified',
  'Proposal Sent',
  'Negotiation',
  'Contract Review',
  'Won',
  'Lost',
];

const opportunitySourceOptions = ['Web', 'Referral', 'Outbound', 'Partner', 'Event', 'Inbound'];
const priorityOptions = ['Low', 'Medium', 'High'];
const lossReasons = ['No Budget', 'Price Too High', 'Competitor Chosen', 'No Response', 'Delayed Project', 'Feature Gap'];
const quoteStatuses = ['Draft', 'Sent', 'Viewed', 'Accepted', 'Rejected', 'Expired'];
const aiRecommendations = [
  'Predict win probability',
  'Identify stalled opportunities',
  'Suggest next action',
  'Generate follow-up email',
  'Summarize meeting notes',
  'Revenue forecasting',
];

const initialOpportunities = [
  {
    id: 'opp-1001',
    name: 'Cloud Migration Program',
    company: 'Northstar Health',
    contact: 'Mia Carter',
    owner: 'Alex Morgan',
    stage: 'Discovery',
    dealValue: 185000,
    expectedRevenue: 148000,
    probability: 35,
    expectedCloseDate: '2026-06-21',
    priority: 'High',
    source: 'Referral',
    competitor: 'Salesforce',
    description: 'Mid-market migration of sales and service operations.',
    tags: ['Enterprise', 'Migration'],
    createdAt: '2026-05-14',
    updatedAt: '2026-06-09',
    auditLogs: [
      { ts: '2026-06-09 10:15', action: 'Moved to Discovery', user: 'Alex Morgan' },
      { ts: '2026-06-08 14:20', action: 'Added note', user: 'Sarah Khan' },
    ],
    activities: [
      { type: 'Email', title: 'Intro email sent', note: 'Sent discovery agenda.', ts: '2026-06-09 09:40' },
      { type: 'Call', title: 'Qualification call', note: 'Budget confirmed; timeline Q3.', ts: '2026-06-08 15:05' },
      { type: 'Meeting', title: 'Discovery meeting', note: 'Stakeholders identified.', ts: '2026-06-07 11:30' },
    ],
    tasks: [
      { title: 'Prepare solution deck', due: '2026-06-11', completed: false },
      { title: 'Send follow-up email', due: '2026-06-10', completed: true },
    ],
    meetings: [
      { title: 'Discovery call', link: 'https://meet.example.com/cloud-migration', notes: 'Align on scope.', outcome: 'Positive', participants: 'Mia, Alex' },
    ],
    quotes: [
      { number: 'Q-2041', status: 'Sent', value: 185000 },
    ],
    files: ['discovery-notes.pdf', 'proposal-outline.pptx'],
    notes: ['Strong executive sponsor.'],
  },
  {
    id: 'opp-1002',
    name: 'CRM Revamp',
    company: 'Apex Manufacturing',
    contact: 'Ethan Lee',
    owner: 'Noah Patel',
    stage: 'Proposal Sent',
    dealValue: 96000,
    expectedRevenue: 76800,
    probability: 60,
    expectedCloseDate: '2026-06-17',
    priority: 'Medium',
    source: 'Outbound',
    competitor: 'HubSpot',
    description: 'Replacing legacy workflows with sales automation.',
    tags: ['SMB', 'Automation'],
    createdAt: '2026-05-24',
    updatedAt: '2026-06-08',
    auditLogs: [{ ts: '2026-06-08 16:45', action: 'Proposal sent', user: 'Noah Patel' }],
    activities: [
      { type: 'Note', title: 'Pricing discussion', note: 'Need revised discount approval.', ts: '2026-06-08 16:30' },
      { type: 'Email', title: 'Proposal emailed', note: 'Included rollout and support.', ts: '2026-06-08 13:10' },
    ],
    tasks: [{ title: 'Revise quote', due: '2026-06-10', completed: false }],
    meetings: [],
    quotes: [{ number: 'Q-2088', status: 'Viewed', value: 96000 }],
    files: ['proposal-v3.pdf'],
    notes: ['Follow up after finance review.'],
  },
  {
    id: 'opp-1003',
    name: 'Support Automation',
    company: 'Vertex Retail',
    contact: 'Olivia Gomez',
    owner: 'Emma Stone',
    stage: 'Negotiation',
    dealValue: 142000,
    expectedRevenue: 113600,
    probability: 75,
    expectedCloseDate: '2026-06-26',
    priority: 'High',
    source: 'Partner',
    competitor: 'Zoho CRM',
    description: 'Automation for support and customer success teams.',
    tags: ['Automation', 'Support'],
    createdAt: '2026-05-10',
    updatedAt: '2026-06-09',
    auditLogs: [{ ts: '2026-06-09 08:45', action: 'Negotiation update', user: 'Emma Stone' }],
    activities: [
      { type: 'Call', title: 'Commercial call', note: 'Negotiating annual commitment.', ts: '2026-06-09 08:40' },
      { type: 'Meeting', title: 'Leadership review', note: 'Positive feedback from COO.', ts: '2026-06-08 12:10' },
    ],
    tasks: [{ title: 'Clarify implementation timeline', due: '2026-06-12', completed: false }],
    meetings: [{ title: 'Commercial review', link: 'https://meet.example.com/support-automation', notes: 'Review SLAs.', outcome: 'Pending', participants: 'Olivia, Emma' }],
    quotes: [{ number: 'Q-2112', status: 'Viewed', value: 142000 }],
    files: ['commercials.xlsx'],
    notes: ['Best chance if pricing is adjusted.'],
  },
  {
    id: 'opp-1004',
    name: 'Customer Expansion',
    company: 'BlueWave Studios',
    contact: 'Sophia Brown',
    owner: 'Alex Morgan',
    stage: 'Won',
    dealValue: 220000,
    expectedRevenue: 220000,
    probability: 100,
    expectedCloseDate: '2026-06-01',
    priority: 'Low',
    source: 'Referral',
    competitor: 'None',
    description: 'Expansion of existing contract across multiple business units.',
    tags: ['Expansion', 'Won'],
    createdAt: '2026-04-02',
    updatedAt: '2026-06-01',
    auditLogs: [{ ts: '2026-06-01 18:05', action: 'Marked won', user: 'Alex Morgan' }],
    activities: [{ type: 'Status', title: 'Deal won', note: 'Contract fully executed.', ts: '2026-06-01 18:00' }],
    tasks: [],
    meetings: [],
    quotes: [{ number: 'Q-1988', status: 'Accepted', value: 220000 }],
    files: ['signed-contract.pdf'],
    notes: ['Fast close after executive alignment.'],
    wonDate: '2026-06-01',
    salesCycleDays: 60,
  },
  {
    id: 'opp-1005',
    name: 'Digital Transformation',
    company: 'Orion Logistics',
    contact: 'Liam Wilson',
    owner: 'Noah Patel',
    stage: 'Lost',
    dealValue: 154000,
    expectedRevenue: 0,
    probability: 0,
    expectedCloseDate: '2026-05-20',
    priority: 'Medium',
    source: 'Event',
    competitor: 'Salesforce',
    description: 'Large transformation initiative with multiple vendors involved.',
    tags: ['Lost', 'Enterprise'],
    createdAt: '2026-03-20',
    updatedAt: '2026-05-22',
    auditLogs: [{ ts: '2026-05-22 11:10', action: 'Marked lost', user: 'Noah Patel' }],
    activities: [{ type: 'Status', title: 'Deal lost', note: 'Competitor selected.', ts: '2026-05-22 11:00' }],
    tasks: [],
    meetings: [],
    quotes: [{ number: 'Q-1930', status: 'Rejected', value: 154000 }],
    files: ['loss-review.pdf'],
    notes: ['Lost on integration depth.'],
    lostDate: '2026-05-22',
    lostReason: 'Competitor Chosen',
  },
];

const newOpportunityTemplate = {
  name: '',
  company: '',
  primaryContact: '',
  stage: 'New Opportunity',
  expectedCloseDate: '',
  dealValue: '',
  owner: '',
  leadSource: 'Web',
  probability: 20,
  competitor: '',
  description: '',
  tags: '',
  attachments: '',
  expectedRevenue: '',
};

const kpiConfig = [
  { key: 'totalOpportunities', label: 'Total Opportunities', icon: <ListAltIcon />, color: '#4B5AB6' },
  { key: 'openOpportunities', label: 'Open Opportunities', icon: <ViewKanbanIcon />, color: '#0288d1' },
  { key: 'pipelineValue', label: 'Pipeline Value', icon: <AttachMoneyIcon />, color: '#388e3c' },
  { key: 'expectedRevenue', label: 'Expected Revenue', icon: <TrendingUpIcon />, color: '#7b1fa2' },
  { key: 'wonOpportunities', label: 'Won Opportunities', icon: <CheckCircleIcon />, color: '#16a34a' },
  { key: 'lostOpportunities', label: 'Lost Opportunities', icon: <CancelIcon />, color: '#dc2626' },
  { key: 'conversionRate', label: 'Conversion Rate', icon: <TimelineIcon />, color: '#f57c00' },
  { key: 'averageDealSize', label: 'Average Deal Size', icon: <BusinessIcon />, color: '#455a64' },
];

const stageColors = {
  'New Opportunity': '#4B5AB6',
  Discovery: '#0288d1',
  Qualified: '#388e3c',
  'Proposal Sent': '#f57c00',
  Negotiation: '#7b1fa2',
  'Contract Review': '#455a64',
  Won: '#16a34a',
  Lost: '#dc2626',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value || 0));

const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  return new Date(dateValue).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const createOppId = () => `opp-${Math.floor(Math.random() * 9000) + 1000}`;

const OpportunityCard = ({ opportunity, index, onOpen, onQuickAction }) => {
  const priorityColor = opportunity.priority === 'High' ? '#dc2626' : opportunity.priority === 'Medium' ? '#f57c00' : '#16a34a';
  return (
    <Draggable draggableId={opportunity.id} index={index}>
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onOpen(opportunity)}
          sx={{
            p: 1.5,
            mb: 1.25,
            borderRadius: 2.5,
            border: `1px solid ${alpha('#000', 0.08)}`,
            cursor: 'pointer',
            boxShadow: snapshot.isDragging ? '0 18px 35px rgba(0,0,0,0.18)' : '0 8px 20px rgba(15,23,42,0.08)',
            transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
            transition: 'all 0.2s ease',
            background: 'linear-gradient(180deg, #fff 0%, #f8fafc 100%)',
            '&:hover': { transform: 'translateY(-2px)' },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }} noWrap>
                {opportunity.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12 }} noWrap>
                {opportunity.company}
              </Typography>
            </Box>
            <Chip
              label={`${opportunity.probability}%`}
              size="small"
              sx={{ bgcolor: alpha(stageColors[opportunity.stage] || '#4B5AB6', 0.12), color: stageColors[opportunity.stage] || '#4B5AB6', fontWeight: 700 }}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.25 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Deal Value</Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatCurrency(opportunity.dealValue)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Close Date</Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatDate(opportunity.expectedCloseDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Owner</Typography>
              <Typography sx={{ fontWeight: 700 }} noWrap>{opportunity.owner}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Priority</Typography>
              <Chip label={opportunity.priority} size="small" sx={{ height: 22, bgcolor: alpha(priorityColor, 0.12), color: priorityColor, fontWeight: 700 }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25, flexWrap: 'wrap' }}>
            {opportunity.tags?.slice(0, 2).map(tag => <Chip key={tag} label={tag} size="small" sx={{ height: 22 }} />)}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
            <Tooltip title="Quick note"><Button size="small" variant="text" onClick={(e) => { e.stopPropagation(); onQuickAction('note', opportunity); }}>Note</Button></Tooltip>
            <Tooltip title="Follow up"><Button size="small" variant="text" onClick={(e) => { e.stopPropagation(); onQuickAction('task', opportunity); }}>Task</Button></Tooltip>
          </Box>
        </Paper>
      )}
    </Draggable>
  );
};

const PipelineColumn = ({ stage, opportunities, onOpen, onQuickAction }) => {
  return (
    <Box sx={{ minWidth: 290, width: { xs: 290, md: 320 }, flex: '0 0 auto' }}>
      <Paper
        sx={{
          p: 1,
          borderRadius: 3,
          background: 'linear-gradient(180deg, #fff 0%, #f8fafc 100%)',
          border: `1px solid ${alpha(stageColors[stage], 0.18)}`,
          boxShadow: '0 12px 32px rgba(15,23,42,0.08)',
          height: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: stageColors[stage] }} />
            <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{stage}</Typography>
          </Box>
          <Chip label={opportunities.length} size="small" sx={{ fontWeight: 700 }} />
        </Box>
        <Droppable droppableId={stage}>
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                minHeight: 300,
                p: 0.5,
                borderRadius: 2,
                background: snapshot.isDraggingOver ? alpha(stageColors[stage], 0.08) : 'transparent',
              }}
            >
              {opportunities.map((opportunity, index) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  index={index}
                  onOpen={onOpen}
                  onQuickAction={onQuickAction}
                />
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </Paper>
    </Box>
  );
};

const Opportunities = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [darkMode, setDarkMode] = useState(false);
  const [activeView, setActiveView] = useState('pipeline');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openCreate, setOpenCreate] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [opportunities, setOpportunities] = useState(initialOpportunities);
  const [aiFeature, setAiFeature] = useState(aiRecommendations[0]);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [createForm, setCreateForm] = useState(newOpportunityTemplate);
  const [notesDraft, setNotesDraft] = useState('');
  const [taskDraft, setTaskDraft] = useState('');
  const [meetingDraft, setMeetingDraft] = useState('');
  const [quoteDraft, setQuoteDraft] = useState({ number: '', status: 'Draft', value: '' });

  useEffect(() => {
    const storedTheme = localStorage.getItem('crmOpportunityTheme');
    if (storedTheme) setDarkMode(storedTheme === 'dark');
  }, []);

  useEffect(() => {
    localStorage.setItem('crmOpportunityTheme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const interval = setInterval(() => setLastRefresh(new Date()), 15000);
    return () => clearInterval(interval);
  }, []);

  const visibleOpportunities = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return opportunities.filter(opp => {
      const matchesSearch = !term || [opp.name, opp.company, opp.contact, opp.owner, String(opp.dealValue), opp.stage].join(' ').toLowerCase().includes(term);
      const matchesStage = stageFilter === 'all' || opp.stage === stageFilter;
      const matchesPriority = priorityFilter === 'all' || opp.priority === priorityFilter;
      const matchesOwner = ownerFilter === 'all' || opp.owner === ownerFilter;
      const matchesSource = sourceFilter === 'all' || opp.source === sourceFilter;
      if (activeView === 'won') return matchesSearch && matchesStage && matchesPriority && matchesOwner && matchesSource && opp.stage === 'Won';
      if (activeView === 'lost') return matchesSearch && matchesStage && matchesPriority && matchesOwner && matchesSource && opp.stage === 'Lost';
      return matchesSearch && matchesStage && matchesPriority && matchesOwner && matchesSource;
    });
  }, [opportunities, searchTerm, stageFilter, priorityFilter, ownerFilter, sourceFilter, activeView]);

  const grouped = useMemo(() => {
    return pipelineStages.reduce((acc, stage) => ({ ...acc, [stage]: visibleOpportunities.filter(opp => opp.stage === stage) }), {});
  }, [visibleOpportunities]);

  const kpis = useMemo(() => {
    const total = visibleOpportunities.length;
    const open = visibleOpportunities.filter(o => !['Won', 'Lost'].includes(o.stage)).length;
    const pipelineValue = visibleOpportunities.filter(o => !['Won', 'Lost'].includes(o.stage)).reduce((sum, o) => sum + Number(o.dealValue || 0), 0);
    const expectedRevenue = visibleOpportunities.reduce((sum, o) => sum + Number(o.expectedRevenue || 0), 0);
    const won = visibleOpportunities.filter(o => o.stage === 'Won').length;
    const lost = visibleOpportunities.filter(o => o.stage === 'Lost').length;
    const conversionRate = total ? Math.round((won / total) * 100) : 0;
    const averageDealSize = total ? Math.round(visibleOpportunities.reduce((sum, o) => sum + Number(o.dealValue || 0), 0) / total) : 0;
    return {
      totalOpportunities: { count: total, revenue: pipelineValue, comparison: '+12%', trend: 'up' },
      openOpportunities: { count: open, revenue: pipelineValue, comparison: '+8%', trend: 'up' },
      pipelineValue: { count: total, revenue: pipelineValue, comparison: '+18%', trend: 'up' },
      expectedRevenue: { count: total, revenue: expectedRevenue, comparison: '+10%', trend: 'up' },
      wonOpportunities: { count: won, revenue: visibleOpportunities.filter(o => o.stage === 'Won').reduce((sum, o) => sum + Number(o.dealValue || 0), 0), comparison: '+5%', trend: 'up' },
      lostOpportunities: { count: lost, revenue: visibleOpportunities.filter(o => o.stage === 'Lost').reduce((sum, o) => sum + Number(o.dealValue || 0), 0), comparison: '-3%', trend: 'down' },
      conversionRate: { count: conversionRate, revenue: 0, comparison: '+2%', trend: 'up' },
      averageDealSize: { count: averageDealSize, revenue: averageDealSize, comparison: '+7%', trend: 'up' },
    };
  }, [visibleOpportunities]);

  const chartLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const revenueForecast = {
    labels: chartLabels,
    datasets: [{
      label: 'Revenue Forecast',
      data: [220000, 260000, 310000, 295000, 345000, 390000],
      borderColor: '#4B5AB6',
      backgroundColor: alpha('#4B5AB6', 0.15),
      tension: 0.35,
      fill: true,
    }],
  };

  const pipelineFunnel = {
    labels: pipelineStages,
    datasets: [{
      label: 'Opportunities',
      data: pipelineStages.map(stage => grouped[stage].length),
      backgroundColor: pipelineStages.map(stage => stageColors[stage]),
      borderRadius: 12,
    }],
  };

  const salespersonPerformance = {
    labels: Array.from(new Set(visibleOpportunities.map(o => o.owner))),
    datasets: [{
      label: 'Won Revenue',
      data: Array.from(new Set(visibleOpportunities.map(o => o.owner))).map(owner => visibleOpportunities.filter(o => o.owner === owner && o.stage === 'Won').reduce((sum, o) => sum + Number(o.dealValue || 0), 0)),
      backgroundColor: '#16a34a',
      borderRadius: 10,
    }],
  };

  const winLossChart = {
    labels: ['Won', 'Lost'],
    datasets: [{
      label: 'Deals',
      data: [kpis.wonOpportunities.count, kpis.lostOpportunities.count],
      backgroundColor: ['#16a34a', '#dc2626'],
      borderRadius: 10,
    }],
  };

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;

    setOpportunities(prev => prev.map(opp => opp.id === draggableId ? { ...opp, stage: destination.droppableId, updatedAt: new Date().toISOString().slice(0, 10), auditLogs: [{ ts: new Date().toISOString().slice(0, 16).replace('T', ' '), action: `Moved to ${destination.droppableId}`, user: 'Current User' }, ...(opp.auditLogs || [])] } : opp));
    setSnackbar({ open: true, message: `Moved to ${destination.droppableId}`, severity: 'success' });
  };

  const handleQuickAction = (type, opportunity) => {
    setSelectedOpportunity(opportunity);
    setOpenDrawer(true);
    setDrawerTab(type === 'task' ? 3 : 4);
    setSnackbar({ open: true, message: `Open ${type} for ${opportunity.name}`, severity: 'success' });
  };

  const handleOpenDetails = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setOpenDrawer(true);
    setDrawerTab(0);
  };

  const handleCreateOpportunity = () => {
    const dealValue = Number(createForm.dealValue || 0);
    const tags = createForm.tags ? createForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
    const attachments = createForm.attachments ? createForm.attachments.split(',').map(t => t.trim()).filter(Boolean) : [];
    const newOpportunity = {
      id: createOppId(),
      name: createForm.name,
      company: createForm.company,
      contact: createForm.primaryContact,
      owner: createForm.owner,
      stage: createForm.stage,
      dealValue,
      expectedRevenue: Number(createForm.expectedRevenue || Math.round(dealValue * (Number(createForm.probability || 20) / 100))),
      probability: Number(createForm.probability || 20),
      expectedCloseDate: createForm.expectedCloseDate,
      priority: Number(createForm.probability || 20) >= 70 ? 'High' : Number(createForm.probability || 20) >= 40 ? 'Medium' : 'Low',
      source: createForm.leadSource,
      competitor: createForm.competitor,
      description: createForm.description,
      tags,
      createdAt: new Date().toISOString().slice(0, 10),
      updatedAt: new Date().toISOString().slice(0, 10),
      auditLogs: [{ ts: new Date().toISOString().slice(0, 16).replace('T', ' '), action: 'Created opportunity', user: 'Current User' }],
      activities: [],
      tasks: [],
      meetings: [],
      quotes: [],
      files: attachments,
      notes: [],
    };
    setOpportunities(prev => [newOpportunity, ...prev]);
    setOpenCreate(false);
    setCreateForm(newOpportunityTemplate);
    setSnackbar({ open: true, message: 'Opportunity created', severity: 'success' });
  };

  const handleExportCSV = () => {
    const headers = ['Opportunity Name', 'Company', 'Stage', 'Deal Value', 'Probability', 'Owner', 'Expected Close Date'];
    const rows = visibleOpportunities.map(o => [o.name, o.company, o.stage, o.dealValue, o.probability, o.owner, o.expectedCloseDate]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'opportunities.csv');
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Opportunities Report', 14, 16);
    doc.setFontSize(10);
    doc.text(`Exported: ${new Date().toLocaleString()}`, 14, 24);
    let y = 34;
    visibleOpportunities.slice(0, 20).forEach((opp, index) => {
      doc.text(`${index + 1}. ${opp.name} | ${opp.company} | ${formatCurrency(opp.dealValue)} | ${opp.stage}`, 14, y);
      y += 7;
      if (y > 280) {
        doc.addPage();
        y = 16;
      }
    });
    doc.save('opportunities.pdf');
  };

  const sectionBg = darkMode ? 'linear-gradient(180deg, #0f172a 0%, #111827 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)';
  const pageCardBg = darkMode ? 'rgba(15,23,42,0.92)' : '#ffffff';
  const textPrimary = darkMode ? '#f8fafc' : '#0f172a';
  const textSecondary = darkMode ? '#cbd5e1' : '#475569';

  return (
    <Box sx={{ minHeight: '100vh', p: { xs: 1.5, sm: 2, md: 3 }, background: sectionBg }}>
      <Paper sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, borderRadius: 4, mb: 2, background: pageCardBg, boxShadow: '0 20px 50px rgba(15,23,42,0.12)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: textPrimary }}>Opportunities</Typography>
            <Typography variant="body2" sx={{ color: textSecondary, mt: 0.5 }}>
              Enterprise CRM pipeline for thousands of deals, with quick actions, AI support, and full activity tracking.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={darkMode ? 'Dark' : 'Light'} onClick={() => setDarkMode(prev => !prev)} sx={{ fontWeight: 700 }} />
            <Tooltip title="Refresh"><Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => setLastRefresh(new Date())}>Refresh</Button></Tooltip>
            <Tooltip title="Export as CSV for Excel"><Button startIcon={<DownloadIcon />} variant="outlined" onClick={handleExportCSV}>Export</Button></Tooltip>
            <Tooltip title="Export PDF"><Button startIcon={<PictureAsPdfIcon />} variant="outlined" onClick={handleExportPDF}>PDF</Button></Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {kpiConfig.map(card => {
          const data = kpis[card.key];
          return (
            <Paper key={card.key} sx={{ p: 2, borderRadius: 3, background: pageCardBg, boxShadow: '0 12px 30px rgba(15,23,42,0.10)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700 }}>{card.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: textPrimary }}>
                    {card.key === 'conversionRate' ? `${data.count}%` : card.key === 'averageDealSize' ? formatCurrency(data.revenue) : data.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: textSecondary }}>{formatCurrency(data.revenue)} revenue</Typography>
                </Box>
                <Box sx={{ color: card.color, bgcolor: alpha(card.color, 0.12), p: 1.2, borderRadius: 2 }}>{card.icon}</Box>
              </Box>
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip size="small" label={`${data.comparison} MoM`} icon={data.trend === 'up' ? <TrendingUpIcon /> : <TrendingDownIcon />} sx={{ fontWeight: 700 }} />
                <Typography variant="caption" sx={{ color: textSecondary }}>Last refresh {lastRefresh.toLocaleTimeString()}</Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>

      <Paper sx={{ p: 1.5, borderRadius: 4, mb: 2, background: pageCardBg, boxShadow: '0 12px 30px rgba(15,23,42,0.10)' }}>
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', xl: 'center' }} justifyContent="space-between">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ flex: 1 }}>
            <TextField
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by opportunity name, company, contact, owner, or deal value"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Button startIcon={<FilterListIcon />} variant="outlined" onClick={(e) => setFilterAnchor(e.currentTarget)}>Filters</Button>
            <Button startIcon={<AutoAwesomeIcon />} variant="outlined" onClick={(e) => setMenuAnchor(e.currentTarget)}>AI Insights</Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenCreate(true)}>+ New Opportunity</Button>
            <Button startIcon={<DownloadIcon />} variant="outlined" onClick={handleExportCSV}>Import</Button>
            <Button startIcon={<DownloadIcon />} variant="outlined" onClick={handleExportCSV}>Export</Button>
          </Stack>
        </Stack>
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{ sx: { p: 1.5, borderRadius: 3, minWidth: 280 } }}>
        <Typography sx={{ fontWeight: 800, px: 1, mb: 1 }}>AI Insights</Typography>
        {aiRecommendations.map(item => (
          <MenuItem key={item} selected={aiFeature === item} onClick={() => { setAiFeature(item); setMenuAnchor(null); setSnackbar({ open: true, message: `${item} activated`, severity: 'success' }); }}>
            <ListItemIcon><LightbulbOutlinedIcon fontSize="small" /></ListItemIcon>
            <Typography variant="body2">{item}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)} PaperProps={{ sx: { p: 1, borderRadius: 3, minWidth: 280 } }}>
        <Typography sx={{ fontWeight: 800, px: 1, mb: 1 }}>Advanced Filters</Typography>
        <Box sx={{ px: 1, py: 0.5 }}>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Stage</InputLabel>
            <Select value={stageFilter} label="Stage" onChange={e => setStageFilter(e.target.value)}>
              <MenuItem value="all">All Stages</MenuItem>
              {pipelineStages.map(stage => <MenuItem key={stage} value={stage}>{stage}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={priorityFilter} label="Priority" onChange={e => setPriorityFilter(e.target.value)}>
              <MenuItem value="all">All Priorities</MenuItem>
              {priorityOptions.map(priority => <MenuItem key={priority} value={priority}>{priority}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Owner</InputLabel>
            <Select value={ownerFilter} label="Owner" onChange={e => setOwnerFilter(e.target.value)}>
              <MenuItem value="all">All Owners</MenuItem>
              {Array.from(new Set(opportunities.map(o => o.owner))).map(owner => <MenuItem key={owner} value={owner}>{owner}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Source</InputLabel>
            <Select value={sourceFilter} label="Source" onChange={e => setSourceFilter(e.target.value)}>
              <MenuItem value="all">All Sources</MenuItem>
              {opportunitySourceOptions.map(source => <MenuItem key={source} value={source}>{source}</MenuItem>)}
            </Select>
          </FormControl>
          <Button fullWidth sx={{ mt: 1 }} variant="text" onClick={() => { setStageFilter('all'); setPriorityFilter('all'); setOwnerFilter('all'); setSourceFilter('all'); }}>Reset</Button>
        </Box>
      </Menu>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.2fr 0.8fr' }, gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 4, background: pageCardBg, boxShadow: '0 12px 30px rgba(15,23,42,0.10)' }}>
          <Typography sx={{ fontWeight: 800, mb: 1.5, color: textPrimary }}>Reports & Analytics</Typography>
          <Stack spacing={2}>
            <Line data={revenueForecast} options={{ responsive: true, plugins: { legend: { display: false } } }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 2 }}>
              <Bar data={pipelineFunnel} options={{ responsive: true, plugins: { legend: { display: false } } }} />
              <Bar data={winLossChart} options={{ responsive: true, plugins: { legend: { display: false } } }} />
              <Bar data={salespersonPerformance} options={{ responsive: true, plugins: { legend: { display: false } } }} />
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 4, background: pageCardBg, boxShadow: '0 12px 30px rgba(15,23,42,0.10)' }}>
          <Typography sx={{ fontWeight: 800, mb: 1.5, color: textPrimary }}>AI Recommendations</Typography>
          <Stack spacing={1.25}>
            <Alert severity="info" icon={<InsightsIcon />} sx={{ borderRadius: 2 }}>Stalled opportunity detected: {visibleOpportunities.find(o => o.stage === 'Proposal Sent')?.name || 'No stalled opportunity'}</Alert>
            <Alert severity="success" icon={<AutoAwesomeIcon />} sx={{ borderRadius: 2 }}>Suggested next action: {aiFeature}</Alert>
            <Alert severity="warning" icon={<TrendingUpIcon />} sx={{ borderRadius: 2 }}>Revenue forecast this month: {formatCurrency(kpis.pipelineValue.revenue + kpis.expectedRevenue.revenue)}</Alert>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 800, mb: 1, color: textPrimary }}>Current focus</Typography>
          <Chip label={aiFeature} color="primary" sx={{ fontWeight: 700 }} />
        </Paper>
      </Box>

      <Paper sx={{ p: 1, borderRadius: 4, background: pageCardBg, boxShadow: '0 12px 30px rgba(15,23,42,0.10)', mb: 2 }}>
        <Tabs value={activeView} onChange={(_, value) => setActiveView(value)} variant={isMobile ? 'scrollable' : 'standard'} scrollButtons="auto">
          <Tab value="pipeline" icon={<ViewKanbanIcon />} iconPosition="start" label="Pipeline View" />
          <Tab value="won" icon={<CheckCircleIcon />} iconPosition="start" label="Won" />
          <Tab value="lost" icon={<CancelIcon />} iconPosition="start" label="Lost" />
        </Tabs>
      </Paper>

      {activeView === 'pipeline' && (
        <Box sx={{ overflowX: 'auto', pb: 1 }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Box sx={{ display: 'flex', gap: 2, minWidth: 2200, pb: 1 }}>
              {pipelineStages.map(stage => (
                <PipelineColumn key={stage} stage={stage} opportunities={grouped[stage] || []} onOpen={handleOpenDetails} onQuickAction={handleQuickAction} />
              ))}
            </Box>
          </DragDropContext>
        </Box>
      )}

      {activeView === 'won' && (
        <Paper sx={{ p: 2, borderRadius: 4, background: pageCardBg, boxShadow: '0 12px 30px rgba(15,23,42,0.10)' }}>
          <Typography sx={{ fontWeight: 800, mb: 1.5, color: textPrimary }}>Won Opportunities</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
            <Paper sx={{ p: 2, borderRadius: 3 }}><Typography variant="caption">Won Revenue</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{formatCurrency(visibleOpportunities.filter(o => o.stage === 'Won').reduce((sum, o) => sum + Number(o.dealValue || 0), 0))}</Typography></Paper>
            <Paper sx={{ p: 2, borderRadius: 3 }}><Typography variant="caption">Won Deals</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{visibleOpportunities.filter(o => o.stage === 'Won').length}</Typography></Paper>
            <Paper sx={{ p: 2, borderRadius: 3 }}><Typography variant="caption">Average Deal Size</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{formatCurrency(visibleOpportunities.filter(o => o.stage === 'Won').reduce((sum, o) => sum + Number(o.dealValue || 0), 0) / Math.max(1, visibleOpportunities.filter(o => o.stage === 'Won').length))}</Typography></Paper>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <TableForOpportunities rows={visibleOpportunities.filter(o => o.stage === 'Won')} type="won" onOpen={handleOpenDetails} />
          </Box>
        </Paper>
      )}

      {activeView === 'lost' && (
        <Paper sx={{ p: 2, borderRadius: 4, background: pageCardBg, boxShadow: '0 12px 30px rgba(15,23,42,0.10)' }}>
          <Typography sx={{ fontWeight: 800, mb: 1.5, color: textPrimary }}>Lost Opportunities</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
            <Paper sx={{ p: 2, borderRadius: 3 }}><Typography variant="caption">Lost Deals</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{visibleOpportunities.filter(o => o.stage === 'Lost').length}</Typography></Paper>
            <Paper sx={{ p: 2, borderRadius: 3 }}><Typography variant="caption">Lost Revenue</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{formatCurrency(visibleOpportunities.filter(o => o.stage === 'Lost').reduce((sum, o) => sum + Number(o.dealValue || 0), 0))}</Typography></Paper>
            <Paper sx={{ p: 2, borderRadius: 3 }}><Typography variant="caption">Loss Rate</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{Math.round((visibleOpportunities.filter(o => o.stage === 'Lost').length / Math.max(1, visibleOpportunities.length)) * 100)}%</Typography></Paper>
          </Box>
          <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Lost Reasons</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {lossReasons.map(reason => <Chip key={reason} label={reason} sx={{ mb: 1 }} />)}
            </Stack>
          </Paper>
          <Box sx={{ overflowX: 'auto' }}>
            <TableForOpportunities rows={visibleOpportunities.filter(o => o.stage === 'Lost')} type="lost" onOpen={handleOpenDetails} />
          </Box>
        </Paper>
      )}

      <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)} PaperProps={{ sx: { width: { xs: '100%', md: 720 }, background: darkMode ? '#0f172a' : '#f8fafc' } }}>
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: darkMode ? '#fff' : '#0f172a' }}>{selectedOpportunity?.name || 'Opportunity Details'}</Typography>
            <Typography variant="body2" sx={{ color: darkMode ? '#cbd5e1' : '#64748b' }}>{selectedOpportunity?.company}</Typography>
          </Box>
          <IconButton onClick={() => setOpenDrawer(false)}><MoreVertIcon /></IconButton>
        </Box>
        <Box sx={{ px: 2.5 }}>
          <Tabs value={drawerTab} onChange={(_, value) => setDrawerTab(value)} variant="scrollable" scrollButtons="auto">
            {['Overview', 'Activities', 'Tasks', 'Meetings', 'Quotes', 'Files', 'Notes', 'History'].map(label => <Tab key={label} label={label} />)}
          </Tabs>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ px: 2.5, pb: 2.5, overflowY: 'auto' }}>
          {drawerTab === 0 && selectedOpportunity && (
            <Stack spacing={2}>
              <Paper sx={{ p: 2, borderRadius: 3 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>Opportunity Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
                  {[
                    ['Deal Value', formatCurrency(selectedOpportunity.dealValue)],
                    ['Expected Revenue', formatCurrency(selectedOpportunity.expectedRevenue)],
                    ['Probability', `${selectedOpportunity.probability}%`],
                    ['Expected Close Date', formatDate(selectedOpportunity.expectedCloseDate)],
                    ['Sales Owner', selectedOpportunity.owner],
                    ['Source', selectedOpportunity.source],
                    ['Created Date', formatDate(selectedOpportunity.createdAt)],
                    ['Last Updated', formatDate(selectedOpportunity.updatedAt)],
                  ].map(([label, value]) => <Box key={label}><Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography><Typography sx={{ fontWeight: 700 }}>{value}</Typography></Box>)}
                </Box>
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 3 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>Quick Actions</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button startIcon={<NotesIcon />} variant="outlined" onClick={() => setDrawerTab(6)}>Add Note</Button>
                  <Button startIcon={<TaskAltIcon />} variant="outlined" onClick={() => setDrawerTab(2)}>Create Task</Button>
                  <Button startIcon={<EventIcon />} variant="outlined" onClick={() => setDrawerTab(3)}>Schedule Meeting</Button>
                  <Button startIcon={<FileUploadIcon />} variant="outlined" onClick={() => setDrawerTab(5)}>Upload Files</Button>
                </Stack>
              </Paper>
            </Stack>
          )}

          {drawerTab === 1 && selectedOpportunity && <TimelineList items={selectedOpportunity.activities || []} title="Newest activity first" />}
          {drawerTab === 2 && (
            <Stack spacing={2}>
              <TextField label="Create Task" value={taskDraft} onChange={e => setTaskDraft(e.target.value)} fullWidth />
              <Button variant="contained" onClick={() => { if (!taskDraft.trim()) return; setSelectedOpportunity(prev => prev ? { ...prev, tasks: [{ title: taskDraft, due: new Date().toISOString().slice(0, 10), completed: false }, ...(prev.tasks || [])] } : prev); setTaskDraft(''); setSnackbar({ open: true, message: 'Task created', severity: 'success' }); }}>Add Task</Button>
              <TimelineList items={selectedOpportunity?.tasks?.map(t => ({ type: 'Task', title: t.title, note: `${t.completed ? 'Completed' : 'Open'} | Due: ${t.due}` })) || []} title="Task list" />
            </Stack>
          )}
          {drawerTab === 3 && (
            <Stack spacing={2}>
              <TextField label="Schedule Meeting" value={meetingDraft} onChange={e => setMeetingDraft(e.target.value)} fullWidth />
              <Button variant="contained" onClick={() => { if (!meetingDraft.trim()) return; setSnackbar({ open: true, message: 'Meeting scheduled', severity: 'success' }); setMeetingDraft(''); }}>Add Meeting</Button>
              <TimelineList items={selectedOpportunity?.meetings?.map(m => ({ type: 'Meeting', title: m.title, note: m.notes })) || []} title="Meeting history" />
            </Stack>
          )}
          {drawerTab === 4 && (
            <Stack spacing={2}>
              <TextField label="Quote Number" value={quoteDraft.number} onChange={e => setQuoteDraft(prev => ({ ...prev, number: e.target.value }))} fullWidth />
              <FormControl fullWidth><InputLabel>Status</InputLabel><Select label="Status" value={quoteDraft.status} onChange={e => setQuoteDraft(prev => ({ ...prev, status: e.target.value }))}>{quoteStatuses.map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}</Select></FormControl>
              <TextField label="Quote Value" value={quoteDraft.value} onChange={e => setQuoteDraft(prev => ({ ...prev, value: e.target.value }))} fullWidth />
              <Button variant="contained" onClick={() => setSnackbar({ open: true, message: 'Quote created', severity: 'success' })}>Save Quote</Button>
              <TimelineList items={selectedOpportunity?.quotes?.map(q => ({ type: 'Quote', title: q.number, note: `${q.status} | ${formatCurrency(q.value)}` })) || []} title="Quote tracking" />
            </Stack>
          )}
          {drawerTab === 5 && <TimelineList items={(selectedOpportunity?.files || []).map(file => ({ type: 'File', title: file, note: 'Available for download' }))} title="Files" />}
          {drawerTab === 6 && (
            <Stack spacing={2}>
              <TextField label="Add note" multiline minRows={3} value={notesDraft} onChange={e => setNotesDraft(e.target.value)} fullWidth />
              <Button variant="contained" onClick={() => { if (!notesDraft.trim()) return; setSelectedOpportunity(prev => prev ? { ...prev, notes: [notesDraft, ...(prev.notes || [])] } : prev); setNotesDraft(''); setSnackbar({ open: true, message: 'Note saved', severity: 'success' }); }}>Save Note</Button>
              <TimelineList items={(selectedOpportunity?.notes || []).map(note => ({ type: 'Note', title: note, note: 'Internal note' }))} title="Notes" />
            </Stack>
          )}
          {drawerTab === 7 && <TimelineList items={selectedOpportunity?.auditLogs || []} title="Audit logs" />}
        </Box>
      </Drawer>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>New Opportunity</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, pt: 1 }}>
            <TextField label="Opportunity Name" required value={createForm.name} onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value }))} fullWidth />
            <TextField label="Company" required value={createForm.company} onChange={e => setCreateForm(prev => ({ ...prev, company: e.target.value }))} fullWidth />
            <TextField label="Primary Contact" required value={createForm.primaryContact} onChange={e => setCreateForm(prev => ({ ...prev, primaryContact: e.target.value }))} fullWidth />
            <FormControl fullWidth><InputLabel>Stage</InputLabel><Select label="Stage" value={createForm.stage} onChange={e => setCreateForm(prev => ({ ...prev, stage: e.target.value }))}>{pipelineStages.map(stage => <MenuItem key={stage} value={stage}>{stage}</MenuItem>)}</Select></FormControl>
            <TextField label="Expected Close Date" type="date" InputLabelProps={{ shrink: true }} value={createForm.expectedCloseDate} onChange={e => setCreateForm(prev => ({ ...prev, expectedCloseDate: e.target.value }))} fullWidth />
            <TextField label="Deal Value" type="number" value={createForm.dealValue} onChange={e => setCreateForm(prev => ({ ...prev, dealValue: e.target.value }))} fullWidth />
            <TextField label="Owner" required value={createForm.owner} onChange={e => setCreateForm(prev => ({ ...prev, owner: e.target.value }))} fullWidth />
            <FormControl fullWidth><InputLabel>Lead Source</InputLabel><Select label="Lead Source" value={createForm.leadSource} onChange={e => setCreateForm(prev => ({ ...prev, leadSource: e.target.value }))}>{opportunitySourceOptions.map(source => <MenuItem key={source} value={source}>{source}</MenuItem>)}</Select></FormControl>
            <TextField label="Probability" type="number" value={createForm.probability} onChange={e => setCreateForm(prev => ({ ...prev, probability: e.target.value }))} fullWidth />
            <TextField label="Competitor" value={createForm.competitor} onChange={e => setCreateForm(prev => ({ ...prev, competitor: e.target.value }))} fullWidth />
            <TextField label="Expected Revenue" type="number" value={createForm.expectedRevenue} onChange={e => setCreateForm(prev => ({ ...prev, expectedRevenue: e.target.value }))} fullWidth />
            <TextField label="Tags" helperText="Comma separated" value={createForm.tags} onChange={e => setCreateForm(prev => ({ ...prev, tags: e.target.value }))} fullWidth />
            <TextField label="Attachments" helperText="Comma separated filenames" value={createForm.attachments} onChange={e => setCreateForm(prev => ({ ...prev, attachments: e.target.value }))} fullWidth />
            <TextField label="Description" multiline minRows={3} value={createForm.description} onChange={e => setCreateForm(prev => ({ ...prev, description: e.target.value }))} fullWidth sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateOpportunity}>Create</Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="left" open={false} onClose={() => {}} />

      <Drawer anchor="right" open={false} onClose={() => {}} />

      <Drawer anchor="bottom" open={false} onClose={() => {}} />

      <Dialog open={snackbar.open} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <DialogTitle>{snackbar.severity === 'success' ? 'Success' : 'Update'}</DialogTitle>
        <DialogContent>{snackbar.message}</DialogContent>
        <DialogActions><Button onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}>Close</Button></DialogActions>
      </Dialog>

      <OpportunitySnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
    </Box>
  );
};

const TableForOpportunities = ({ rows, type, onOpen }) => {
  return (
    <Box sx={{ minWidth: 900 }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1.3fr 1fr 1fr 1fr 1fr 1fr', gap: 1, p: 1.25, bgcolor: 'rgba(75,90,182,0.06)', fontWeight: 800 }}>
            <Box>Opportunity Name</Box><Box>Company</Box><Box>{type === 'lost' ? 'Lost Value' : 'Won Value'}</Box><Box>{type === 'lost' ? 'Lost Date' : 'Won Date'}</Box><Box>{type === 'lost' ? 'Lost Reason' : 'Sales Owner'}</Box><Box>{type === 'lost' ? 'Competitor' : 'Sales Cycle Days'}</Box><Box>{type === 'lost' ? 'Owner' : 'Analytics'}</Box>
          </Box>
          {rows.map(row => (
            <Box key={row.id} onClick={() => onOpen(row)} sx={{ display: 'grid', gridTemplateColumns: '2fr 1.3fr 1fr 1fr 1fr 1fr 1fr', gap: 1, p: 1.25, borderTop: '1px solid rgba(0,0,0,0.06)', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(75,90,182,0.04)' } }}>
              <Box sx={{ fontWeight: 700 }}>{row.name}</Box>
              <Box>{row.company}</Box>
              <Box>{formatCurrency(row.dealValue)}</Box>
              <Box>{formatDate(row.stage === 'Won' ? row.wonDate : row.lostDate || row.expectedCloseDate)}</Box>
              <Box>{type === 'lost' ? row.lostReason || '—' : row.owner}</Box>
              <Box>{type === 'lost' ? row.competitor || '—' : row.salesCycleDays ? `${row.salesCycleDays} days` : '—'}</Box>
              <Box>{type === 'lost' ? row.owner : `${row.probability}%`}</Box>
            </Box>
          ))}
        </Paper>
      </Box>
    </Box>
  );
};

const TimelineList = ({ items = [], title }) => (
  <Paper sx={{ p: 2, borderRadius: 3 }}>
    <Typography sx={{ fontWeight: 800, mb: 1.5 }}>{title}</Typography>
    <Stack spacing={1}>
      {items.length === 0 && <Alert severity="info">No items yet.</Alert>}
      {items.map((item, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700 }}>{item.title || item.action}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.note || item.user || ''}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.ts || ''}</Typography>
        </Paper>
      ))}
    </Stack>
  </Paper>
);

const OpportunitySnackbar = ({ open, message, severity, onClose }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
    <DialogTitle sx={{ fontWeight: 800 }}>{severity === 'success' ? 'Success' : 'Notice'}</DialogTitle>
    <DialogContent><Typography>{message}</Typography></DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>
);

export default Opportunities;
